import { useEffect, useState, useRef } from "react";
import { Grid, Box, Paper, Button, IconButton } from "@mui/material";
import { styled } from "@mui/system";
import DashboardOrders from "../../components/rl-dashboard-components/orders";
import Inventory from "../../components/rl-dashboard-components/inventory";
import OrdersStatus from "../../components/rl-dashboard-components/orders-status";
import Delivery from "../../components/rl-dashboard-components/delivery";
import SLACompliance from "../../components/rl-dashboard-components/sla-compliance";
import OrderFillRate from "../../components/rl-dashboard-components/order-fill-rate";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { statsService } from "../../utils/services/stats-service";
import html2canvas from "html2canvas";
import SyncIcon from "@mui/icons-material/Sync";
import CustomTypography from "../../globalStyles/fonts";
import { CircularProgress, Backdrop } from "@mui/material";
import PdfIcon from "../../globalStyles/icons/pdf-icon";
import ImageIcon from "../../globalStyles/icons/image-icon";
import ClientAutocomplete from "../../components/clientAutocomplete";
import { isMobile } from "../../globalStyles/mobile";
import CatalogAnalyticsChart from "../../components/dashboard/CatalogAnalyticsChart";
import wsManager from "../../utils/websocketManager";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  cursor: "pointer",
  textAlign: "center",
  borderRadius: "15px",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  border: "2px solid var(--border-color)",
  backgroundColor: "#f5f5f5",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    // transform: "translateY(-5px)",
    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.2)",
  },
}));

const DashboardScreen = () => {
  const [clientFilter, setClientFilter] = useState(null);
  const client_id = localStorage.getItem("client_id");
  const [stats, setStats] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(new Date());
  const [orderRangeStats, setOrderRangeStats] = useState({});
  const [currentWeekStatusCounts, setCurrentWeekStatusCounts] = useState([]);
  const [selectedRange, setSelectedRange] = useState("Year");
  const [orderStatusByRange, updateOrderStatusByRange] = useState({});
  const [orderSLAByRange, setOrderSLAByRange] = useState({});
  const [exporting, setExporting] = useState(false);

  // Catalog Analytics State
  const today = new Date();
  const [catalogDateRange, setCatalogDateRange] = useState({
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: today,
    key: "selection",
  });
  const [selectedCatalog, setSelectedCatalog] = useState("all");
  const [selectedCatalogPage, setSelectedCatalogPage] = useState(null);
  const [selectedCatalogStyle, setSelectedCatalogStyle] = useState(null);
  const [selectedCatalogSku, setSelectedCatalogSku] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogRevenueChart, setCatalogRevenueChart] = useState([]);
  const [catalogMetrics, setCatalogMetrics] = useState([]);
  const [catalogTopPageAOV, setCatalogTopPageAOV] = useState([]);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const dashboardRef = useRef(null);
  const [selectedClientData, setSelectedClientData] = useState(null);

  // Helper function to get the current client ID
  const getCurrentClientId = () => {
    return selectedClientData?._id || client_id;
  };

  const fetchStats = async (signal) => {
    try {
      const currentClientId = getCurrentClientId();
      const response = await statsService.fetchStats(
        {
          client: currentClientId,
          selectedOrderDate: selectedDate,
          range: selectedRange,
        },
        signal,
      );
      setStats(response);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchCurrentWeekStatusCounts = async (signal) => {
    try {
      const currentClientId = getCurrentClientId();
      const response = await statsService.fetchCurrentWeekStatusCounts(
        {
          client_id: currentClientId,
          range: selectedRange,
          selectedDate,
        },
        signal,
      );
      setCurrentWeekStatusCounts(response.statusCounts || []);
    } catch (e) {
      console.error("Error fetching current week status counts:", e);
    }
  };

  const fetchStatusesOrderByRange = async (signal) => {
    try {
      const currentClientId = getCurrentClientId();
      const response = await statsService.fetchOrderStatusByRange(
        {
          range: selectedRange,
          client_id: currentClientId,
          selectedDate,
        },
        signal,
      );
      updateOrderStatusByRange(response);
    } catch (e) {
      console.error("Error fetching order by range:", e);
    }
  };

  const onChangeSelectedRange = async (signal) => {
    const currentClientId = getCurrentClientId();
    const resp = await statsService.fetchOrderByRange(
      {
        range: selectedRange,
        client_id: currentClientId,
        selectedDate: selectedDate,
      },
      signal,
    );
    setOrderRangeStats(resp);
  };

  const fetchOrderSLAByRange = async (signal) => {
    try {
      const currentClientId = getCurrentClientId();
      const response = await statsService.fetchOrderSLAByRange(
        {
          range: selectedRange,
          client_id: currentClientId,
          selectedDate: selectedDate,
        },
        signal,
      );
      setOrderSLAByRange(response);
    } catch (e) {
      console.error("Error fetching order SLA by range:", e);
    }
  };

  // Add this function to the component
  const exportDashboardAsPDF = async () => {
    setExporting(true);
    try {
      const currentClientId = getCurrentClientId();
      // Collect all the relevant dashboard data
      const dashboardData = {
        stats,
        orderRangeStats,
        orderStatusByRange,
        currentWeekStatusCounts,
        orderSLAByRange,
        selectedRange,
        clientInfo: {
          id: currentClientId,
          name:
            selectedClientData?.name ||
            localStorage.getItem("client_name") ||
            "Client",
        },
        timestamp: new Date().toISOString(),
        revenueChart: catalogRevenueChart,
        catalogMetrics,
        topPageAOVKpi: catalogTopPageAOV,
        catalogDateRange,
        selectedCatalog,
        selectedCatalogPage,
        selectedCatalogStyle,
        selectedCatalogSku,
      };

      // Send the data to the server for PDF generation
      const response = await statsService.exportAsPDF(dashboardData);

      if (response.status === "success") {
        let blob;

        try {
          // Try to decode as base64
          const binaryString = window.atob(response.pdfBuffer);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          blob = new Blob([bytes], { type: "application/pdf" });
        } catch (e) {
          console.log("Not a valid base64 string, trying alternative method");
          // If not base64, try to handle as binary data
          if (typeof response.pdfBuffer === "string") {
            // If it's a string but not base64, create a text blob
            blob = new Blob([response.pdfBuffer], { type: "application/pdf" });
          } else if (
            response.pdfBuffer instanceof ArrayBuffer ||
            response.pdfBuffer instanceof Uint8Array
          ) {
            // If it's already binary data
            blob = new Blob([response.pdfBuffer], { type: "application/pdf" });
          } else {
            throw new Error("Unexpected response format");
          }
        }
        if (blob) {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = response.fileName || "dashboard-export.pdf";
          document.body.appendChild(link); // Add to DOM
          link.click(); // Trigger download
          document.body.removeChild(link); // Clean up
          URL.revokeObjectURL(link.href); // Clean up object URL
        }
      }
    } catch (error) {
      console.error("Error exporting dashboard as PDF:", error);
      // You could show an error notification here
    } finally {
      setExporting(false);
    }
  };

  // Sync function to refresh all data
  const handleSync = async () => {
    const controller = new AbortController();
    const signal = controller.signal;
    setSyncing(true);
    try {
      await Promise.all([
        fetchStats(),
        onChangeSelectedRange(),
        fetchCurrentWeekStatusCounts(),
        fetchStatusesOrderByRange(),
        fetchOrderSLAByRange(),
      ]);
    } catch (error) {
      console.error("Error syncing data:", error);
    } finally {
      setSyncing(false);
    }
  };

  const exportDashboardAsImage = () => {
    setExporting(true);

    // Create a timestamp for the filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const fileName = `dashboard-export-${timestamp}.png`;

    if (dashboardRef.current) {
      // Save original styles to restore later
      const originalHeight = dashboardRef.current.style.height;
      const originalOverflow = dashboardRef.current.style.overflow;
      const originalPosition = dashboardRef.current.style.position;

      // Temporarily modify the element to ensure all content is visible
      dashboardRef.current.style.height = "auto";
      dashboardRef.current.style.overflow = "visible";
      dashboardRef.current.style.position = "relative";

      // Add a small delay to ensure charts (Chart.js) have fully rendered
      // This is especially important for CatalogAnalyticsChart
      setTimeout(() => {
        html2canvas(dashboardRef.current, {
          scrollX: 0,
          scrollY: -window.scrollY,
          windowWidth: document.documentElement.offsetWidth,
          windowHeight: document.documentElement.scrollHeight,
          scale: 2, // Higher resolution
          useCORS: true, // Enable CORS for images
          allowTaint: true,
          backgroundColor: "#ffffff",
          onclone: (clonedDoc) => {
            // Find the cloned element to manipulate
            const clonedElement = clonedDoc.querySelector(
              '[data-dashboard-clone="true"]',
            );
            if (clonedElement) {
              clonedElement.style.height = "auto";
              clonedElement.style.overflow = "visible";
            }
          },
        })
          .then((canvas) => {
            // Restore original styles
            dashboardRef.current.style.height = originalHeight;
            dashboardRef.current.style.overflow = originalOverflow;
            dashboardRef.current.style.position = originalPosition;

            setExporting(false);

            // Convert to blob and download
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  // Create download link and trigger download
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = fileName;
                  link.click();

                  // Clean up
                  URL.revokeObjectURL(link.href);
                }
              },
              "image/png",
              1.0,
            );
          })
          .catch((error) => {
            // Restore original styles on error
            dashboardRef.current.style.height = originalHeight;
            dashboardRef.current.style.overflow = originalOverflow;
            dashboardRef.current.style.position = originalPosition;

            console.error("Error exporting dashboard:", error);
            setExporting(false);
          });
      }, 500); // Wait 500ms for chart rendering
    }
  };

  useEffect(() => {
    fetchStats();
    onChangeSelectedRange();
    fetchCurrentWeekStatusCounts();
    fetchStatusesOrderByRange();
    fetchOrderSLAByRange();
  }, [selectedClientData, selectedRange, selectedDate]);

  // Listen for WebSocket stats refresh events
  useEffect(() => {
    const unsubscribe = wsManager.on("statsRefresh", (message) => {
      const freshData = message?.data;
      if (!freshData) return;

      // Update all stats state with the new data from WebSocket
      console.log(
        "📡 [Dashboard] Received statsRefresh via WebSocket",
        freshData,
      );

      setStats({
        averagePrice: freshData.averagePrice,
        totalOrders: freshData.totalOrders,
        deliveredOrders: freshData.deliveredOrders,
        monthComparision: freshData.monthComparision,
        weekComparision: freshData.weekComparision,
        totalInventory: freshData.totalInventory,
        newOrders: freshData.newOrders,
      });
    });

    return () => unsubscribe();
  }, []);

  const handleClientFilterChange = (client) => {
    setClientFilter(client);
    setSelectedClientData(client);
  };
  const theme = useTheme();
  const isBelow1500 = useMediaQuery("(max-width:1500px)");
  const isBelow1350 = useMediaQuery("(max-width:1350px)");
  const isTablet = useMediaQuery(theme.breakpoints.down("md")); // md = 900px
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm")); // sm = 600px

  const customScrollbarStyle = {
    height: isMobileScreen ? "calc(100vh - 120px)" : "calc(100vh - 100px)",
    padding: "0px",
    overflowX: "hidden",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#273894 #D9D9D9",
    "&::-webkit-scrollbar": {
      width: "7px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#D9D9D9",
      borderRadius: "14px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#273894",
      borderRadius: "14px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#273894",
    },
  };

  return (
    <>
      {/* Full-screen loading overlay */}
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={syncing}
      >
        <CircularProgress size={60} sx={{ color: "#0F3659" }} />
        <CustomTypography
          style={{
            fontSize: 16,
            color: "#0F3659",
            fontWeight: 600,
          }}
        >
          Syncing Dashboard Data...
        </CustomTypography>
      </Backdrop>

      <Box style={{ paddingLeft: 0, width: "100%" }} sx={customScrollbarStyle}>
        <Box
          sx={{
            display: "flex",
            //  alignItems: "center",
            padding: "8px 8px 0 3px",
            width: "99%",
            flexDirection: isMobileScreen ? "column" : "row",
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <CustomTypography
              style={{
                fontSize: isMobileScreen ? 14 : 18,
                textAlign: "start",
                fontWeight: 500,
              }}
            >
              Insights Summary
            </CustomTypography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "flex-end",
              width: "100%",
              mt: isMobile() ? 1 : 0,
            }}
          >
            {!client_id && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <ClientAutocomplete
                  borderColor={"#0F3659"}
                  inputStyle={{ marginBottom: 8 }}
                  selectedClient={selectedClientData}
                  onClientChange={(client) => {
                    handleClientFilterChange(client);
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: isMobile() ? 1 : 2,
                justifyContent: "flex-end",
              }}
            >
              {isMobile() ? (
                <IconButton onClick={exportDashboardAsImage}>
                  <ImageIcon fill={"#0F3659"} />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={exportDashboardAsImage}
                  startIcon={<ImageIcon fill={"#0F3659"} />}
                  disabled={exporting || syncing}
                  sx={{
                    backgroundColor: "var(--bg-paper)",
                    "&:hover": { backgroundColor: "rgb(221, 239, 255)" },
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 600,
                    border: "1px solid #0F3659",
                    color: "var(--text-heading)",
                    height: "40px",
                    minHeight: "40px",
                  }}
                >
                  {exporting ? "Exporting..." : "Export"}
                </Button>
              )}
              {isMobile() ? (
                <IconButton onClick={exportDashboardAsPDF}>
                  <PdfIcon fill={"#0F3659"} />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={exportDashboardAsPDF}
                  startIcon={<PdfIcon fill={"#ffff"} />}
                  disabled={exporting || syncing}
                  sx={{
                    backgroundColor: "#0F3659",
                    "&:hover": { backgroundColor: "#1c2a70" },
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 600,
                    height: "40px",
                    minHeight: "40px",
                  }}
                >
                  {exporting ? "Exporting..." : "Export"}
                </Button>
              )}
              {isMobile() ? (
                <IconButton onClick={handleSync}>
                  {syncing ? (
                    <CircularProgress size={20} sx={{ color: "#0F3659" }} />
                  ) : (
                    <SyncIcon sx={{ color: "#0F3659" }} />
                  )}
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleSync}
                  disabled={syncing || exporting}
                  sx={{
                    backgroundColor: "#0F3659",
                    borderColor: "#0F3659",
                    color: "#0F3659",
                    "&:hover": {
                      borderColor: "#1c2a70",
                      backgroundColor: "#1c2a70",
                    },
                    borderRadius: "5px",
                    textTransform: "none",
                    fontWeight: 600,
                    width: "40px",
                    minWidth: "40px",
                    height: "40px",
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {syncing ? (
                    <CircularProgress size={20} sx={{ color: "#FFF" }} />
                  ) : (
                    <SyncIcon sx={{ color: "#FFF", fontSize: "20px" }} />
                  )}
                </Button>
              )}
            </Box>
          </Box>
        </Box>

        <Grid
          style={{ paddingLeft: 0 }}
          container
          spacing={2}
          sx={{
            width: "99%",
            margin: 0,
            height: "100%",
            flexWrap: "wrap",
          }}
          ref={dashboardRef}
          data-dashboard-clone="true"
        >
          {/* Catalog Analytics Section */}
          <Grid style={{ paddingLeft: 0 }} item xs={12}>
            <CatalogAnalyticsChart
              clientId={getCurrentClientId()}
              backgroundColor={"var(--bg-paper)"}
              border={"1px solid var(--border-color)"}
              minHeight={isMobileScreen ? 240 : 400}
              dateRange={catalogDateRange}
              onDateRangeChange={setCatalogDateRange}
              selectedCatalog={selectedCatalog}
              onCatalogChange={setSelectedCatalog}
              selectedPage={selectedCatalogPage}
              onPageChange={setSelectedCatalogPage}
              selectedStyle={selectedCatalogStyle}
              onStyleChange={setSelectedCatalogStyle}
              selectedSku={selectedCatalogSku}
              onSkuChange={setSelectedCatalogSku}
              loading={catalogLoading}
              onLoadingChange={setCatalogLoading}
              revenueChart={catalogRevenueChart}
              onRevenueChartChange={setCatalogRevenueChart}
              catalogMetrics={catalogMetrics}
              onCatalogMetricsChange={setCatalogMetrics}
              topPageAOV={catalogTopPageAOV}
              onTopPageAOVChange={setCatalogTopPageAOV}
              catalogOptions={catalogOptions}
              onCatalogOptionsChange={setCatalogOptions}
            />
          </Grid>

          {/* First row: DashboardOrders and Inventory side by side */}
          <Grid container item xs={12} spacing={2} style={{ paddingLeft: 0 }}>
            <Grid
              item
              xs={isMobileScreen ? 12 : 12}
              md={isMobileScreen ? 12 : isBelow1350 ? 12 : 8}
              lg={isMobileScreen ? 12 : isBelow1350 ? 12 : 8}
            >
              <DashboardOrders
                totalOrders={stats.totalOrders}
                totalDelivery={stats.deliveredOrders}
                totalInventory={stats.totalInventory}
                newOrders={stats.newOrders}
                onChangeSelectedRange={(range) => setSelectedRange(range)}
                orderRangeStats={orderRangeStats}
                setSelectedDate={setSelectedDate}
                selectedDate={selectedDate}
                enableCalendar={true}
              />
            </Grid>
            <Grid
              item
              xs={isMobileScreen ? 12 : 12}
              md={isMobileScreen ? 12 : isBelow1350 ? 12 : 4}
              lg={isMobileScreen ? 12 : isBelow1350 ? 12 : 4}
            >
              <Inventory clientId={getCurrentClientId()} />
            </Grid>
          </Grid>
          <Grid container item xs={12} spacing={2} style={{ paddingLeft: 0 }}>
            <Grid
              item
              xs={isMobileScreen ? 12 : 12}
              md={isMobileScreen ? 12 : isBelow1350 ? 8 : 8}
              lg={isMobileScreen ? 12 : isBelow1350 ? 8 : 8}
            >
              <OrdersStatus data={orderStatusByRange?.intervalCounts} />
            </Grid>
            <Grid
              item
              xs={isMobileScreen ? 12 : 12}
              md={isMobileScreen ? 12 : isBelow1350 ? 4 : 4}
              lg={isMobileScreen ? 12 : isBelow1350 ? 4 : 4}
            >
              <Delivery
                data_obj={currentWeekStatusCounts || []}
                selectedDate={selectedDeliveryDate}
                setSelectedDate={setSelectedDeliveryDate}
                onDateChange={(date) => setSelectedDeliveryDate(date)}
              />
            </Grid>
            {/* <Grid
              item
              xs={isMobileScreen ? 12 : 6}
              md={isMobileScreen ? 12 : isBelow1350 ? 6 : 3}
              lg={isMobileScreen ? 12 : isBelow1350 ? 6 : 3}
            >
              <SLACompliance
                data={[
                  {
                    name: "Within SLA",
                    value: orderSLAByRange?.endToEnd
                      ? orderSLAByRange?.endToEnd[0]?.withinSLA || 0
                      : 0,
                  },
                  {
                    name: "Outside SLA",
                    value: orderSLAByRange?.endToEnd
                      ? orderSLAByRange?.endToEnd[0]?.outsideSLA || 0
                      : 0,
                  },
                ]}
              />
            </Grid> */}
          </Grid>
          <Grid style={{ paddingLeft: 0 }} item xs={isMobileScreen ? 12 : 12}>
            <OrderFillRate
              client_id={getCurrentClientId()}
              selectedDate={selectedDate}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default DashboardScreen;
